import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LoginRequest } from '../models/login-request.model';
import { Observable, tap, catchError, throwError, BehaviorSubject } from 'rxjs';
import { LoginResponse, User } from '../models/login-response.model';
import { GenerateCodeRequest } from '../models/generate-code-request';
import { GenerateCodeResponse } from '../models/generate-code-response';
import { VerifyCodeRequest } from '../models/verify-code-request.model';
import { VerifyCodeResponse } from '../models/verify-code-response';
import { ResetPassRequest } from '../models/reset-pass-request';
import { ResetPassResponse } from '../models/reset-pass-response';
import { LoaderService } from '../../../shared/services/loader.service';
import { ThemeService } from '../../../shared/services/theme.service';
import { ServicesService } from '../../services/services/services.service';
import { CatalogService } from '../../dashboard/assistances/services/catalog.service';
import * as localforage from 'localforage';

/**
 * Servicio de autenticación que maneja el login, tokens y sesiones de usuario.
 * Utiliza localforage como almacenamiento principal con localStorage como fallback
 * para mantener compatibilidad síncrona con el código existente.
 *
 * @author Sistema Ike Now
 * @version 2.0
 * @since 2025
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** URL base de la API para autenticación */
  private readonly baseUrl = environment.apiBaseUrl;

  /** Clave de encriptación para datos sensibles */
  private readonly encryptionKey = 'bXlfc3VwM3JfU2VjdXJlX0tleV8yMDI1';

  /** Subject para manejar el refresh de tokens de forma reactiva */
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  /** Flag para evitar múltiples requests de refresh simultáneos */
  private isRefreshing = false;

  /**
   * Cache síncrono en memoria para mantener compatibilidad con la interfaz síncrona.
   * Los datos se cargan desde localforage al inicializar y se mantienen sincronizados.
   */
  private cache: {
    accessToken?: string | null;
    refreshToken?: string | null;
    idToken?: string | null;
    user?: User | null;
    cognito?: string | null;
    tokenExpiration?: number | null;
    google_key?: string | null;
    account?: string | null;
    sessionActive?: boolean;
    lastRoute?: string | null;
    [key: string]: any;
  } = {};

  /** Flag que indica si el cache ha sido inicializado desde localforage */
  private cacheInitialized = false;

  /**
   * Constructor del servicio de autenticación.
   * Inicializa el cache desde localforage y configura el refresh automático de tokens.
   *
   * @param http - Cliente HTTP para realizar peticiones a la API
   * @param router - Router de Angular para navegación
   * @param loaderService - Servicio para mostrar/ocultar el loader
   * @param themeService - Servicio para manejo de temas
   */
  constructor(
    private http: HttpClient,
    private router: Router,
    private loaderService: LoaderService,
    private themeService: ThemeService,
    private servicesService: ServicesService,
    private catalogService: CatalogService
  ) {
    this.initializeCache();
    this.initializeTokenRefresh();
  }

  // Métodos auxiliares para manejar localforage con caché síncrono
  private async initializeCache(): Promise<void> {
    if (this.cacheInitialized) return;

    try {
      // Cargar datos desde localforage al caché
      const keys = ['accessToken', 'refreshToken', 'idToken', 'user', 'cognito', 'tokenExpiration', 'google_key', 'account', 'sessionActive'];

      for (const key of keys) {
        const value = await localforage.getItem(key);
        if (value !== null) {
          this.cache[key] = value;
        } else {
          // Si no hay en localforage, cargar desde localStorage
          const fallbackValue = localStorage.getItem(key);
          if (fallbackValue) {
            try {
              this.cache[key] = JSON.parse(fallbackValue);
            } catch {
              this.cache[key] = fallbackValue;
            }
            // Migrar a localforage
            await localforage.setItem(key, this.cache[key]);
          }
        }
      }

      this.cacheInitialized = true;
    } catch (error) {
      console.error('Error initializing cache:', error);
      // Fallback: cargar solo desde localStorage
      this.loadFromLocalStorage();
      this.cacheInitialized = true;
    }
  }

  private loadFromLocalStorage(): void {
    this.cache.accessToken = localStorage.getItem('accessToken');
    this.cache.refreshToken = localStorage.getItem('refreshToken');
    this.cache.idToken = localStorage.getItem('idToken');
    this.cache.user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;
    this.cache.cognito = localStorage.getItem('cognito');
    this.cache.tokenExpiration = localStorage.getItem('tokenExpiration') ? parseInt(localStorage.getItem('tokenExpiration')!) : null;
    this.cache.google_key = localStorage.getItem('google_key');
    this.cache.account = localStorage.getItem('account');
    this.cache.sessionActive = localStorage.getItem('sessionActive') === 'true';
  }

  private async setItem(key: string, value: any): Promise<void> {
    // Actualizar caché inmediatamente
    this.cache[key] = value;

    try {
      await localforage.setItem(key, value);
    } catch (error) {
      console.error(`Error setting ${key}:`, error);
    }

    // También actualizar localStorage como backup
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  }

  private getItem(key: string): any {
    // Inicializar caché si no está listo
    if (!this.cacheInitialized) {
      this.loadFromLocalStorage();
      this.cacheInitialized = true;
    }

    return this.cache[key] || null;
  }

  private async removeItem(key: string): Promise<void> {
    // Limpiar del caché
    delete this.cache[key];

    try {
      await localforage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key}:`, error);
    }

    // También limpiar de localStorage
    localStorage.removeItem(key);
  }

  private async clear(): Promise<void> {
    // Limpiar caché
    this.cache = {};

    try {
      await localforage.clear();
    } catch (error) {
      console.error('Error clearing localforage:', error);
    }

    // También limpiar localStorage
    localStorage.clear();
  }

  postLogin(payload: LoginRequest): Observable<LoginResponse> {
    // Limpiar caché de servicios antes de hacer login con nueva cuenta
    this.servicesService.clearCache();
    this.catalogService.clearCache();
    
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/login/assistancePortal`, payload)
      .pipe(
        tap((res) => {
          this.storeAuthData(res);
        })
      );
  }

  private storeAuthData(res: LoginResponse): void {
    const expirationTime = Date.now() + 60 * 60 * 1000; // 1 hora

    // Almacenar en localforage
    this.setItem('accessToken', res.data.accessToken);
    this.setItem('refreshToken', res.data.refreshToken);
    this.setItem('idToken', res.data.idToken);
    this.setItem('user', res.user);
    this.setItem('cognito', res.user.cognito);
    this.setItem('tokenExpiration', expirationTime);
    this.setItem('google_key', this.encryptData(res.google_key));
    //this.setItem('google_key', this.encryptData('AIzaSyCpXWnh8cap1AehzARTooqx9aAyuMnaqdM'));
    this.setItem('account', res.account);
    this.setItem('sessionActive', true);

    // Persistencia adicional para iOS PWA
    this.forceSessionPersistence();

    // Cargar tema después del login
    const accountId = parseInt(res.account);
    if (accountId) {
      this.themeService.loadThemeConfig(accountId).subscribe({
        next: (themeResponse) => {
          if (themeResponse.theme && themeResponse.theme.length > 0) {
            this.themeService.applyTheme(themeResponse.theme);
          }
        },
        error: (error) => {
          console.error('Error al cargar el tema:', error);
        },
      });
    }
  }

  getToken(): string | null {
    return this.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return this.getItem('refreshToken');
  }

  getGoogleApiKey(): string | null {
    const encryptedKey = this.getItem('google_key');
    return encryptedKey ? this.decryptData(encryptedKey) : null;
  }

  private encryptData(data: string): string {
    try {
      return btoa(
        data
          .split('')
          .map((char, i) =>
            String.fromCharCode(
              char.charCodeAt(0) ^
                this.encryptionKey.charCodeAt(i % this.encryptionKey.length)
            )
          )
          .join('')
      );
    } catch {
      return data;
    }
  }

  private decryptData(encryptedData: string): string {
    try {
      const decoded = atob(encryptedData);
      return decoded
        .split('')
        .map((char, i) =>
          String.fromCharCode(
            char.charCodeAt(0) ^
              this.encryptionKey.charCodeAt(i % this.encryptionKey.length)
          )
        )
        .join('');
    } catch {
      return encryptedData;
    }
  }

  getAccount(): string | null {
    return this.getItem('account');
  }

  getCurrentUser(): User | null {
    return this.getItem('user');
  }

  logout(): void {
    this.themeService.clearTheme();

    // Limpiar caché de servicios
    this.servicesService.clearCache();
    this.catalogService.clearCache();

    // Limpiar caché en memoria
    this.cache = {};
    this.cacheInitialized = false;

    // Limpiar localforage y localStorage completamente
    this.clear();

    // Limpiar TODOS los datos adicionales
    const additionalKeys = [
      'ios-pwa-access-token',
      'ios-pwa-refresh-token',
      'ios-pwa-session-active',
      //'backup-uuidc',
      //'ios-pwa-uuidc',
      'servicesContact',
      'solicitudData',
      'catalogoCiudades',
      'catalogoMunicipios',
      //'ProductByAccount',
      'selectedPoliza',
      'currentPolizaId',
      'profileData',
      'legalAssistanceId',
      'legalAssistancefecha',
      'code',
      'email'
    ];
    additionalKeys.forEach(key => {
      localStorage.removeItem(key);
      localforage.removeItem(key).catch(() => {});
    });

    // Limpiar sessionStorage también
    sessionStorage.clear();
  }

  isSessionActive(): boolean {
    return this.getItem('sessionActive') === true &&
           !!this.getRefreshToken() &&
           !!this.getToken();
  }

  saveLastRoute(route: string): void {
    if (route && route !== '/' && route !== '/login') {
      this.setItem('lastRoute', route);
    }
  }

  getLastRoute(): string | null {
    return this.getItem('lastRoute');
  }

  // Método específico para iOS PWA que verifica múltiples veces el localStorage
  isSessionActiveForIOSPWA(): Promise<boolean> {
    return new Promise((resolve) => {
      const checkSession = (attempts = 0) => {
        const sessionActive = localStorage.getItem('sessionActive') === 'true';
        const hasRefreshToken = !!this.getRefreshToken();
        const hasToken = !!this.getToken();

        if ((sessionActive && hasRefreshToken && hasToken) || attempts >= 3) {
          resolve(sessionActive && hasRefreshToken && hasToken);
        } else {
          setTimeout(() => checkSession(attempts + 1), 50);
        }
      };
      checkSession();
    });
  }

  // Método para forzar la persistencia en iOS PWA
  forceSessionPersistence(): void {
    const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone;
    if (isIOSPWA) {
      // Duplicar datos críticos en diferentes keys para iOS PWA
      const accessToken = this.getToken();
      const refreshToken = this.getRefreshToken();

      if (accessToken) localStorage.setItem('ios-pwa-access-token', accessToken);
      if (refreshToken) localStorage.setItem('ios-pwa-refresh-token', refreshToken);
      localStorage.setItem('ios-pwa-session-active', 'true');
    }
  }

  // Método para recuperar tokens en iOS PWA si los principales fallan
  recoverTokensForIOSPWA(): boolean {
    const isIOSPWA = /iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone;
    if (!isIOSPWA) return false;

    const backupAccessToken = localStorage.getItem('ios-pwa-access-token');
    const backupRefreshToken = localStorage.getItem('ios-pwa-refresh-token');
    const backupSessionActive = localStorage.getItem('ios-pwa-session-active');

    if (backupAccessToken && backupRefreshToken && backupSessionActive === 'true') {
      localStorage.setItem('accessToken', backupAccessToken);
      localStorage.setItem('refreshToken', backupRefreshToken);
      localStorage.setItem('sessionActive', 'true');
      return true;
    }

    return false;
  }

  isTokenExpired(): boolean {
    const expiration = this.getItem('tokenExpiration');
    if (!expiration) return true;
    return Date.now() > expiration;
  }

  private initializeTokenRefresh(): void {
    // Verificar si hay tokens al inicializar
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();

    if (token && refreshToken) {
      this.scheduleTokenRefresh();
    }
  }

  private scheduleTokenRefresh(): void {
    const expiration = this.getItem('tokenExpiration');
    if (!expiration) return;

    const expirationTime = expiration;
    const currentTime = Date.now();
    const timeUntilRefresh = expirationTime - currentTime - (5 * 60 * 1000); // 5 minutos antes

    if (timeUntilRefresh > 0) {
      setTimeout(() => {
        this.refreshAccessToken().subscribe();
      }, timeUntilRefresh);
    } else {
      // Token ya expiró o está por expirar, renovar inmediatamente
      this.refreshAccessToken().subscribe();
    }
  }

  refreshAccessToken(): Observable<any> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.asObservable();
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logoutAndRedirect();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<LoginResponse>(`${this.baseUrl}/login/refresh`, {
      refreshToken: refreshToken
    }).pipe(
      tap((response) => {
        this.isRefreshing = false;
        this.storeAuthData(response);
        this.refreshTokenSubject.next(response.data.accessToken);
        this.scheduleTokenRefresh();
      }),
      catchError((error) => {
        this.isRefreshing = false;
        this.logoutAndRedirect();
        return throwError(() => error);
      })
    );
  }

  canRefreshToken(): boolean {
    const refreshToken = this.getRefreshToken();
    return !!refreshToken;
  }

  logoutAndRedirect(): void {
    this.loaderService.hide();
    this.logout();
    this.router.navigate(['/']);
  }

  generateCode(payload: GenerateCodeRequest): Observable<GenerateCodeResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<GenerateCodeResponse>(
      `${this.baseUrl}/code/generate`,
      payload,
      { headers }
    );
  }

  verifyCode(payload: VerifyCodeRequest): Observable<VerifyCodeResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<VerifyCodeResponse>(
      `${this.baseUrl}/code/checkCodeUser`,
      payload,
      { headers }
    );
  }

  resetPassword(payload: ResetPassRequest): Observable<ResetPassResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const url = `${this.baseUrl}/code/validate`;
    return this.http.post<ResetPassResponse>(url, payload, { headers });
  }
}
